// ─────────────────────────────────────────────────────────────────────────────
// graphLayout — stable macro geography. The map must be learnable.
//
// THE GRAPH IS NOT A SCREENSAVER. A borough is in the same place today, after a
// reload, in a headless capture and on another machine, because every position
// is either an explicit anchor from the data or a hash of the node's own id.
// There is no Math.random in this file and no live force simulation anywhere in
// G0 — a layout that reshuffles is a map nobody can learn.
//
// ORDER OF AUTHORITY, highest first:
//   1. layout.pinned + x/y/z          — an explicit world anchor
//   2. layout.azimuth/elevation/radius — an explicit described position
//   3. layout.ring/order               — an explicit slot in a ring
//   4. hash of the node id             — the deterministic fallback
// The fallback is TEMPORARY. The final macro ordering arrives with the
// Mermaid/blast-radius data as explicit anchors, and then rule 1 or 2 wins
// without a line of this file changing.
//
// Sibling order is the sorted id list, never insertion order: a node that
// arrives later in a patch must not shuffle its siblings around it.
// ─────────────────────────────────────────────────────────────────────────────

import { RexyGraphStore } from './graphStore';
import { RexyGraphNodeRecord } from './graphTypes';
import { TAU, hashUnit, lerp, localBasis, sampleEllipsoidVolume, sphericalToCartesian } from './graphMath';

export type Vec3 = [number, number, number];
export type GraphPositions = Map<string, Vec3>;

/** City Hall's anchor. The one position in the graph that is a constant. */
export const CITY_HALL_POSITION: Vec3 = [0, 0, 0];

/** Domains sit on a shell around City Hall at approximately this radius. */
export const DOMAIN_RING_RADIUS = 600;

/**
 * TRUE SPHERICAL BOROUGH SHELL — three latitude bands, three slots each.
 *
 * The previous ring-plus-jitter was a perturbed ring, and a perturbed ring
 * presents edge-on as a slab however much jitter it carries. Latitude bands put
 * genuine structure above, level with and below the observer, so the macro
 * object reads volumetric from every angle and the camera has somewhere to go in
 * all three axes.
 *
 * Deterministic slots, not a simulation: a borough's bearing AND its latitude are
 * both learnable, which is the whole point of a stable geography.
 */
// Exported for the live structural adapter's dynamic-slot branch: when the real
// domain count is not nine it supplies explicit azimuth/elevation anchors, and
// those must land on the SAME shell the three-band system uses or live and
// synthetic geographies would sit at different scales. Value unchanged.
export const DOMAIN_SHELL_RADIUS = 610;
const DOMAIN_RADIUS_VARIATION = 0.08;
const DOMAIN_LATITUDES = [0.62, 0.0, -0.62];
/** Per-band azimuth offsets, so the three bands do not stack into columns. */
const DOMAIN_BAND_AZIMUTH = [0.2, 1.2, 0.55];
const DOMAIN_SLOTS_PER_BAND = 3;
/** Small local perturbation only — the bands already supply the depth. */
const DOMAIN_ANCHOR_JITTER = 25;

/**
 * Local cloud radii by parent kind: [x, y, z]. ELLIPSOIDS, NOT SPHERES.
 * Equal radii on a shell is what produced the spoke-and-bead reading; distinct
 * axes plus volume sampling is what makes a mansion look like a place.
 */
const CLOUD_RADII: { [kind: string]: [number, number, number] } = {
  domain: [145, 95, 125],
  agent: [62, 38, 55],
  'memory-cluster': [32, 18, 27],
  'memory-system': [30, 17, 25],
  'city-hall': [110, 70, 95],
};
const DEFAULT_CLOUD_RADII: [number, number, number] = [48, 30, 42];

/**
 * Explicit anchor, if the data supplied one. Returns null when the node has no
 * opinion and the deterministic fallback should decide.
 */
function explicitPosition(node: RexyGraphNodeRecord, origin: Vec3): Vec3 | null {
  const hint = node.layout;
  if (!hint) return null;

  if (hint.pinned && typeof hint.x === 'number' && typeof hint.y === 'number' && typeof hint.z === 'number') {
    return [hint.x, hint.y, hint.z];
  }

  if (typeof hint.azimuth === 'number' && typeof hint.elevation === 'number') {
    const radius = typeof hint.radius === 'number' ? hint.radius : DOMAIN_RING_RADIUS;
    const out: Vec3 = [0, 0, 0];
    sphericalToCartesian(hint.azimuth, hint.elevation, radius, out);
    return [origin[0] + out[0], origin[1] + out[1], origin[2] + out[2]];
  }

  return null;
}

/**
 * The boroughs. A stable angular order (sorted ids), alternating elevation
 * bands, and a hash-derived wobble on both radius and angle so the shell reads
 * as organic rather than as a clock face — while landing in exactly the same
 * place every time.
 */
function layoutDomains(ids: string[], store: RexyGraphStore, out: GraphPositions): void {
  const sorted = ids.slice().sort();

  for (let i = 0; i < sorted.length; i++) {
    const id = sorted[i];
    const node = store.getNode(id);
    if (!node) continue;

    const explicit = explicitPosition(node, CITY_HALL_POSITION);
    if (explicit) {
      out.set(id, explicit);
      continue;
    }

    // `order` is the stable synthetic slot the seed assigns; it maps straight
    // onto band and slot so the geography never depends on sort accidents.
    const slot = node.layout && typeof node.layout.order === 'number' ? node.layout.order : i;
    const band = Math.floor(slot / DOMAIN_SLOTS_PER_BAND) % DOMAIN_LATITUDES.length;
    const within = slot % DOMAIN_SLOTS_PER_BAND;

    const latitude = DOMAIN_LATITUDES[band];
    const azimuth = DOMAIN_BAND_AZIMUTH[band] + within * ((Math.PI * 2) / DOMAIN_SLOTS_PER_BAND);

    // ±8% radius, signed from the id's own hash.
    const signed = hashUnit(id, 'shell-radius') * 2 - 1;
    const radius = DOMAIN_SHELL_RADIUS * (1 + signed * DOMAIN_RADIUS_VARIATION);

    const cosLat = Math.cos(latitude);
    out.set(id, [
      radius * cosLat * Math.cos(azimuth) + (hashUnit(id, 'jx') - 0.5) * 2 * DOMAIN_ANCHOR_JITTER,
      radius * Math.sin(latitude) + (hashUnit(id, 'jy') - 0.5) * 2 * DOMAIN_ANCHOR_JITTER,
      radius * cosLat * Math.sin(azimuth) + (hashUnit(id, 'jz') - 0.5) * 2 * DOMAIN_ANCHOR_JITTER,
    ]);
  }
}

/**
 * Children as a deterministic ELLIPSOID VOLUME in the parent's own local frame.
 * Each child's position comes from a hash of its own id, so adding one child by
 * patch never moves its siblings at all.
 */
function layoutChildren(parentId: string, store: RexyGraphStore, out: GraphPositions): void {
  const parent = store.getNode(parentId);
  const origin = out.get(parentId);
  if (!parent || !origin) return;

  const children = store.childrenOf(parentId).slice().sort();
  const n = children.length;
  if (!n) return;

  const radii = CLOUD_RADII[parent.kind] || DEFAULT_CLOUD_RADII;

  // The cloud is oriented along the line from its own parent, so a borough's
  // constellation leans away from City Hall instead of every cloud sharing the
  // world axes. Stable per parent, so the shape is learnable.
  const grandparentId = parent.parentId;
  const grandOrigin = grandparentId ? out.get(grandparentId) : undefined;
  const right: Vec3 = [1, 0, 0];
  const up: Vec3 = [0, 1, 0];
  const forward: Vec3 = [0, 0, 1];
  localBasis(grandOrigin || [0, 0, 0], origin, right, up, forward);

  // 0.82–1.18 per parent, so no two clouds are the same size.
  const scale = lerp(0.82, 1.18, hashUnit(parentId, 'cloud-scale'));
  const local: Vec3 = [0, 0, 0];

  for (let i = 0; i < n; i++) {
    const id = children[i];
    const node = store.getNode(id);
    if (!node) continue;

    // Already placed by a higher-authority pass (the borough shell). A second
    // placement here would silently overwrite the macro geography.
    if (out.has(id)) continue;

    const explicit = explicitPosition(node, origin);
    if (explicit) {
      out.set(id, explicit);
      continue;
    }

    sampleEllipsoidVolume(id, radii[0] * scale, radii[1] * scale, radii[2] * scale, local);

    out.set(id, [
      origin[0] + right[0] * local[0] + up[0] * local[1] + forward[0] * local[2],
      origin[1] + right[1] * local[0] + up[1] * local[1] + forward[1] * local[2],
      origin[2] + right[2] * local[0] + up[2] * local[1] + forward[2] * local[2],
    ]);
  }
}

/**
 * Resolve every node's position, parents before children.
 *
 * G0 runs this on the main thread: the lab graph is small and the work is a few
 * trig calls per node. G1 moves it into graphLayout.worker.ts and returns
 * transferable typed arrays — the ORDER OF AUTHORITY above is what moves across
 * unchanged, which is why it lives in a pure function with no Three.js in sight.
 */
export function computeLayout(store: RexyGraphStore): GraphPositions {
  const positions: GraphPositions = new Map();

  // 1 — roots. City Hall is pinned at the origin; any other parentless node is
  // treated as a borough-level anchor rather than being dropped.
  const cityHalls: string[] = [];
  const rootDomains: string[] = [];
  const otherRoots: string[] = [];

  store.forEachNode((node) => {
    if (node.parentId && store.getNode(node.parentId)) return;
    if (node.kind === 'city-hall') cityHalls.push(node.id);
    else if (node.kind === 'domain') rootDomains.push(node.id);
    else otherRoots.push(node.id);
  });

  for (let i = 0; i < cityHalls.length; i++) {
    const node = store.getNode(cityHalls[i]);
    const explicit = node ? explicitPosition(node, CITY_HALL_POSITION) : null;
    positions.set(cityHalls[i], explicit || [CITY_HALL_POSITION[0], CITY_HALL_POSITION[1], CITY_HALL_POSITION[2]]);
  }

  // 2 — boroughs: children of City Hall, plus any parentless domain.
  const domainIds: string[] = rootDomains.slice();
  for (let i = 0; i < cityHalls.length; i++) {
    const kids = store.childrenOf(cityHalls[i]);
    for (let k = 0; k < kids.length; k++) {
      const kid = store.getNode(kids[k]);
      if (kid && kid.kind === 'domain') domainIds.push(kids[k]);
    }
  }
  layoutDomains(domainIds, store, positions);

  // Parentless non-domain nodes — orphans. They get the outer shell rather than
  // the origin, so "unattached" is visible instead of hidden under City Hall.
  const orphans = otherRoots.slice().sort();
  for (let i = 0; i < orphans.length; i++) {
    const id = orphans[i];
    const p: Vec3 = [0, 0, 0];
    sphericalToCartesian(
      hashUnit(id, 'orphan-az') * TAU,
      (hashUnit(id, 'orphan-el') - 0.5) * 1.4,
      DOMAIN_RING_RADIUS * lerp(1.55, 1.95, hashUnit(id, 'orphan-r')),
      p,
    );
    positions.set(id, p);
  }

  // 3 — breadth-first through the hierarchy. A child is never placed before the
  // frame it belongs to exists.
  const frontier: string[] = [];
  for (let i = 0; i < cityHalls.length; i++) frontier.push(cityHalls[i]);
  for (let i = 0; i < domainIds.length; i++) frontier.push(domainIds[i]);
  for (let i = 0; i < orphans.length; i++) frontier.push(orphans[i]);

  let cursor = 0;
  while (cursor < frontier.length) {
    const parentId = frontier[cursor++];
    layoutChildren(parentId, store, positions);
    const kids = store.childrenOf(parentId);
    for (let k = 0; k < kids.length; k++) {
      if (positions.has(kids[k])) frontier.push(kids[k]);
    }
  }

  return positions;
}
